import { DEFAULT_OG_IMAGE, SITE_URL } from './site.mjs';

export const ROBOTS_INDEX =
    'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
export const ROBOTS_NOINDEX = 'noindex, follow, noarchive';

export const CLAIM_TRUTH_CLASSES = {
    REAL_NOW: 'real now',
    PROTOTYPE_NOW: 'prototype now',
    ESTIMATED_DIRECTIONAL: 'estimated / directional',
    ROADMAP: 'roadmap',
};

export const CLAIM_TRUTH_NOTES = {
    [CLAIM_TRUTH_CLASSES.REAL_NOW]:
        'Use only for foundations that already exist and can be described as current reality.',
    [CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW]:
        'Use for current flows or capabilities that exist today but are not yet framed as finished product behavior.',
    [CLAIM_TRUTH_CLASSES.ESTIMATED_DIRECTIONAL]:
        'Use for current direction, planned commercial framing, and any details that may still shift before launch.',
    [CLAIM_TRUTH_CLASSES.ROADMAP]:
        'Use for future platform layers, later polish, and post-launch maturity that must not be presented as already done.',
};

export const CANONICAL_ROUTE_ORDER = [
    '/',
    '/avenbox',
    '/technology',
    '/security',
    '/use-cases',
    '/pricing',
    '/faq',
    '/about',
    '/contact',
];

export const PAGE_CONFIG_BY_ROUTE = {
    '/': {
        path: '/',
        canonicalPath: '/',
        title: 'AvenBox | The Sovereign Control Layer for AI',
        description:
            'AvenBox is the sovereign control layer for AI — a private, local-first system that keeps serious work under user control with trusted execution and continuity.',
        h1: 'The intelligence layer that stays with you.',
        routePurpose: 'Authority page',
        breadcrumbName: 'Home',
        image: DEFAULT_OG_IMAGE,
        schemas: ['organization', 'website', 'breadcrumb'],
        truthClasses: [
            CLAIM_TRUTH_CLASSES.REAL_NOW,
            CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW,
            CLAIM_TRUTH_CLASSES.ROADMAP,
        ],
        robots: ROBOTS_INDEX,
        indexable: true,
    },
    '/avenbox': {
        path: '/avenbox',
        canonicalPath: '/avenbox',
        title: 'AvenBox | Sovereign AI Control System',
        description:
            'Understand AvenBox as a portable private AI control system built around direct interaction, trusted continuity, and deliberate routing.',
        h1: 'AvenBox',
        routePurpose: 'Product page',
        breadcrumbName: 'AvenBox',
        image: DEFAULT_OG_IMAGE,
        schemas: ['organization', 'website', 'breadcrumb'],
        truthClasses: [
            CLAIM_TRUTH_CLASSES.REAL_NOW,
            CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW,
            CLAIM_TRUTH_CLASSES.ROADMAP,
        ],
        robots: ROBOTS_INDEX,
        indexable: true,
    },
    '/technology': {
        path: '/technology',
        canonicalPath: '/technology',
        title: 'How AvenBox Works | Aven Studios',
        description:
            'Understand the current AvenBox architecture: local execution, orchestration, trust boundaries, trusted extension, and continuity across trusted environments.',
        h1: 'How AvenBox Works',
        routePurpose: 'Proof page',
        breadcrumbName: 'Technology',
        image: DEFAULT_OG_IMAGE,
        schemas: ['organization', 'website', 'breadcrumb'],
        truthClasses: [
            CLAIM_TRUTH_CLASSES.REAL_NOW,
            CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW,
            CLAIM_TRUTH_CLASSES.ROADMAP,
        ],
        robots: ROBOTS_INDEX,
        indexable: true,
    },
    '/security': {
        path: '/security',
        canonicalPath: '/security',
        title: 'Security & Privacy | Aven Studios',
        description:
            'See how AvenBox treats privacy as architecture, where the trust boundary sits, and how current protections are separated from later hardening work.',
        h1: 'Security & Privacy',
        routePurpose: 'Trust page',
        breadcrumbName: 'Security',
        image: DEFAULT_OG_IMAGE,
        schemas: ['organization', 'website', 'breadcrumb'],
        truthClasses: [
            CLAIM_TRUTH_CLASSES.REAL_NOW,
            CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW,
            CLAIM_TRUTH_CLASSES.ROADMAP,
        ],
        robots: ROBOTS_INDEX,
        indexable: true,
    },
    '/use-cases': {
        path: '/use-cases',
        canonicalPath: '/use-cases',
        title: 'Use Cases | AvenBox for Serious Work',
        description:
            'See why AvenBox is aimed first at privacy-sensitive professionals, founders, operators, and technical users doing serious work under real trust constraints.',
        h1: 'Use Cases',
        routePurpose: 'Authority + translation page',
        breadcrumbName: 'Use Cases',
        image: DEFAULT_OG_IMAGE,
        schemas: ['organization', 'website', 'breadcrumb'],
        truthClasses: [
            CLAIM_TRUTH_CLASSES.REAL_NOW,
            CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW,
            CLAIM_TRUTH_CLASSES.ROADMAP,
        ],
        robots: ROBOTS_INDEX,
        indexable: true,
    },
    '/pricing': {
        path: '/pricing',
        canonicalPath: '/pricing',
        title: 'Pricing & Reservation | AvenBox',
        description:
            'Review the current AvenBox reservation tracks, what the EUR 1 reservation means and does not mean, and how pricing and timing are being framed toward Q1 2027.',
        h1: 'Pricing & Reservation',
        routePurpose: 'Conversion page',
        breadcrumbName: 'Pricing',
        image: DEFAULT_OG_IMAGE,
        schemas: ['organization', 'website', 'breadcrumb'],
        truthClasses: [
            CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW,
            CLAIM_TRUTH_CLASSES.ESTIMATED_DIRECTIONAL,
            CLAIM_TRUTH_CLASSES.ROADMAP,
        ],
        robots: ROBOTS_INDEX,
        indexable: true,
    },
    '/faq': {
        path: '/faq',
        canonicalPath: '/faq',
        title: 'Frequently Asked Questions | AvenBox',
        description:
            'Direct answers about AvenBox, including what is real now, current prototype, estimated or directional, and roadmap.',
        h1: 'Frequently Asked Questions',
        routePurpose: 'Support page',
        breadcrumbName: 'FAQ',
        image: DEFAULT_OG_IMAGE,
        schemas: ['organization', 'website', 'faqpage', 'breadcrumb'],
        truthClasses: [
            CLAIM_TRUTH_CLASSES.REAL_NOW,
            CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW,
            CLAIM_TRUTH_CLASSES.ESTIMATED_DIRECTIONAL,
            CLAIM_TRUTH_CLASSES.ROADMAP,
        ],
        robots: ROBOTS_INDEX,
        indexable: true,
    },
    '/about': {
        path: '/about',
        canonicalPath: '/about',
        title: 'About Aven Studios | AvenBox',
        description:
            'Meet Aven Studios and the founder behind AvenBox, and understand the company thesis behind control, privacy, and the next trust boundary in AI.',
        h1: 'About Aven Studios',
        routePurpose: 'Trust page',
        breadcrumbName: 'About',
        image: DEFAULT_OG_IMAGE,
        schemas: ['organization', 'website', 'person', 'breadcrumb'],
        truthClasses: [
            CLAIM_TRUTH_CLASSES.REAL_NOW,
            CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW,
            CLAIM_TRUTH_CLASSES.ROADMAP,
        ],
        robots: ROBOTS_INDEX,
        indexable: true,
    },
    '/contact': {
        path: '/contact',
        canonicalPath: '/contact',
        title: 'Contact | Aven Studios',
        description:
            'Reach Aven Studios through clear general, press, investor, or reservation paths — and learn how to reserve a position in the first production run of AvenBox.',
        h1: 'Contact',
        routePurpose: 'Conversion / trust page',
        breadcrumbName: 'Contact',
        image: DEFAULT_OG_IMAGE,
        schemas: ['organization', 'website', 'breadcrumb'],
        truthClasses: [
            CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW,
            CLAIM_TRUTH_CLASSES.ESTIMATED_DIRECTIONAL,
            CLAIM_TRUTH_CLASSES.ROADMAP,
        ],
        robots: ROBOTS_INDEX,
        indexable: true,
    },
    '/news': {
        path: '/news',
        canonicalPath: '/news',
        title: 'Updates | Aven Studios',
        description:
            'External signal on AI privacy, sovereignty, and regulation — curated reporting with our take on why local-first architecture is becoming load-bearing.',
        h1: 'Updates are not yet public',
        routePurpose: 'Retired page',
        breadcrumbName: 'Updates',
        image: DEFAULT_OG_IMAGE,
        schemas: [],
        truthClasses: [CLAIM_TRUTH_CLASSES.ROADMAP],
        robots: ROBOTS_NOINDEX,
        indexable: false,
    },
};

export const INDEXABLE_ROUTES = CANONICAL_ROUTE_ORDER.filter(
    (route) => PAGE_CONFIG_BY_ROUTE[route]?.indexable
);

export const PRIMARY_NAV_ROUTES = [
    '/avenbox',
    '/technology',
    '/security',
    '/use-cases',
    '/pricing',
    '/faq',
    '/about',
    '/contact',
];

export const LEGACY_ROUTE_REDIRECTS = {
    '/solutions': '/technology',
    '/about-us': '/about',
    '/projects': '/avenbox',
    '/model': '/avenbox',
};

export function getPageConfig(path) {
    return PAGE_CONFIG_BY_ROUTE[path] || PAGE_CONFIG_BY_ROUTE['/'];
}

export function getCanonicalPath(path) {
    return getPageConfig(path).canonicalPath || '/';
}

export function getCanonicalUrl(path) {
    const canonicalPath = getCanonicalPath(path);
    return canonicalPath === '/' ? `${SITE_URL}/` : `${SITE_URL}${canonicalPath}`;
}
